<script setup lang="ts">
import { ref } from 'vue';
import { useMutation } from '@tanstack/vue-query';
import { patientTasksApi, type PatientTask } from '@/api/patient-tasks';

interface Props {
  patientTask: PatientTask;
}

interface Emits {
  (e: 'close'): void;
  (e: 'success'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const submitButtonDisabled = ref(false);

const discardMutation = useMutation({
  mutationFn: () => patientTasksApi.destroy(props.patientTask.id),
  onSuccess: () => {
    emit('success');
    emit('close');
  },
  onError: () => {
    submitButtonDisabled.value = false;
  },
});

function handleSubmit() {
  submitButtonDisabled.value = true;
  discardMutation.mutate();
}
</script>
<template>
  <v-card class="pa-4">
    <v-card-title>
      Archivar Tarea "{{ patientTask.taskDefinitionName }}"
    </v-card-title>
    <v-card-text>
      <p class="mb-4">
        ¿Está seguro que desea archivar la tarea "{{ patientTask.taskDefinitionName }}"
        programada para las {{ patientTask.humanScheduledTime }}?
      </p>
      <p class="mb-4 text-body-2 text-medium-emphasis">
        La tarea será archivada y no aparecerá en la lista de tareas activas del paciente.
        Esta acción se puede revertir más tarde si es necesario.
      </p>
      <v-alert
        type="info"
        variant="tonal"
        class="mb-4"
      >
        <strong>Categoría:</strong> {{ patientTask.taskCategoryName }}<br>
        <strong>Hora programada:</strong> {{ patientTask.humanScheduledTime }}<br>
        <strong>Asignado por:</strong> {{ patientTask.assignedByEmail }}
        <div v-if="patientTask.observations">
          <strong>Observaciones:</strong> {{ patientTask.observations }}
        </div>
      </v-alert>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        :disabled="discardMutation.isPending.value"
        @click="emit('close')"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="warning"
        :loading="discardMutation.isPending.value"
        :disabled="submitButtonDisabled"
        @click="handleSubmit"
      >
        Archivar
      </v-btn>
    </v-card-actions>
    <v-alert
      v-if="discardMutation.isError.value"
      type="error"
      variant="tonal"
      class="mt-4"
    >
      Ha ocurrido un error al archivar la tarea. Intenta nuevamente.
    </v-alert>
  </v-card>
</template>
